import React from "react";
import { Card, Box, Typography } from "@mui/material";
import { COLORS } from "../../../theme/landingTheme";
import { IconDark } from "./IconNode.jsx";

/*
 * FeatureCard
 * Small reusable card used on the homepage feature list.
 *
 * Props
 * - icon: React component (rendered via IconDark wrapper)
 * - title: string (main heading)
 * - subtitle: string (optional description under the title)
 * - gradient: optional gradient override (not currently used here,
 *   kept for future theming/extensions)
 *
 * Notes
 * - Uses `IconDark` to render the provided icon inside a themed circle.
 * - Subtitle is rendered only when provided; typography scales responsively.
 */

const FeatureCard = ({ icon: Icon, title, subtitle, gradient }) => {
  // Fallback gradient (kept for potential future use)
  const fallbackGradient = COLORS.GRADIENTS.MAIN();

  return (
    <Card
      variant="whiteCardColorfulShadow"
      sx={{
        width: { xs: "100%", sm: 300 },
        height: { xs: 250, md: 300 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 1.25,
        textAlign: "center",
        p: { xs: 3, md: 4 },
        border: "1px solid rgba(63,154,130,0.14)",
        transition: "transform .2s ease, box-shadow .2s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 20px 40px rgba(16,24,40,0.12)",
        },
      }}
    >
      <IconDark icon={Icon} />

      <Typography
        variant="h6"
        sx={{
          color: "text.primary",
          fontWeight: 700,
          fontSize: { xs: "1.2rem", md: "1.35rem" },
          letterSpacing: "-0.2px",
        }}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: { xs: "0.95rem", md: "1rem" },
            lineHeight: 1.5,
            maxWidth: 360,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Card>
  );
};

export default FeatureCard;
